import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { iconClockTeal, iconInfo } from "assets";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Badge, Card, Picture } from "components/UI";
import { displayTimeFormat, displayDayDateFormat } from "utils/datetime";
import { urlHandler } from "utils/helper";
import qs from "query-string";
import { BackgroundSection, labelBg } from "../LandingPage/common";
import MemberAccessBadge from "./MemberAccessBadge";
import CountdownLabel from "./Countdown";
import { useUpcomingInternalEvent } from "hooks/useUpcomingInternalEvent";

const EventCard = ({ events, eventsPath }) => {
  const {
    id,
    city,
    name,
    image_url: imageUrl,
    start_at: startAt,
    end_at: endAt,
    start_date: startDateFallback,
    current_user_rsvped: rsvped,
    event_category: eventCategory,
    time_zone: timeZone,
    member_access: memberAccess,
    highlight_message,
    type
  } = events;
  const eventType = eventCategory?.name || "Festival";
  const { isInternalEvent, config, countdownValue } = useUpcomingInternalEvent({ type, startAt, endAt })
  const isHappeningEvent = countdownValue?.status === 'happening_now';
  const eventTypeUrl = `${eventsPath}?${qs.stringify({ category: eventType })}`;
  const startDate = displayDayDateFormat(startAt || startDateFallback, timeZone);
  const endDate = displayDayDateFormat(endAt, timeZone);
  const timeValue = (() => {
    if (!startAt) return null;
    const start = displayTimeFormat(startAt);
    return endAt > startAt && startDate === endDate
      ? `${start} - ${displayTimeFormat(endAt)}`
      : start;
  })();

  return (
    <div className="relative z-0 h-full">
      <Link to={`${eventsPath}/${id}`} className="block h-full">
        <Card className="w-full mb-10 sm:mb-0 h-full flex flex-col">
          <BackgroundSection
            backgroundImage={`url(${urlHandler(imageUrl)})`}
            className="relative bg-no-repeat bg-cover bg-center h-56 rounded-t-lg flex-shrink-0"
          >
            <div className="flex justify-between">
              <Link to={eventTypeUrl}>
                <Badge bg={labelBg(eventType)} className="absolute z-10 ml-4 mt-4">
                  {eventType}
                </Badge>
              </Link>
              {rsvped && (
                <div className="flex items-center justify-center mr-4 mt-4 bg-brilliantGreen text-white h-8 w-20 rounded-full">
                  Booked
                </div>
              )}
            </div>
          </BackgroundSection>
          <div className="p-4 flex flex-col flex-1">
            <div className="flex text-boulder text-sm mb-3">
              {startDate && (
                <span
                  className={`flex-1 pr-2 text-easter${timeValue ? " border-r border-easter" : ""}`}
                >
                  {startDate}
                </span>
              )}
              {timeValue && (
                <span className="flex items-center flex-1 pl-2 text-easter">
                  <Picture src={iconClockTeal} className="w-4 mr-2 text-easter" />
                  {timeValue}
                </span>
              )}
            </div>
            {isInternalEvent && <div className={`my-2 ${isHappeningEvent ? 'text-center' : ''}`}>
              <CountdownLabel startAt={startAt} endAt={endAt} labelClassName='text-sm sm:text-xxs lg:text-sm' />
            </div>}
            <div className="mb-2">
              <h3 className="font-lynstone pr-2">{name}</h3>
            </div>
            <div className="flex justify-between mb-3 mt-1">
              <p className="text-boulder font-semibold">{city?.name || "Global"}</p>
              {!!memberAccess && <MemberAccessBadge memberAccess={memberAccess} />}
            </div>
            {isInternalEvent && highlight_message && config && <div className={`flex items-center gap-2 border border-blue-200 bg-blue-100 rounded-md p-2 text-xs mb-3 mt-1`}>
              <ErrorOutlineIcon className={`flex-shrink-0 text-blue-600`} />
              <span className={`${config.messageColorClass}`}>{highlight_message}</span>
            </div>}
          </div>
        </Card>
      </Link>
    </div>
  );
};

EventCard.propTypes = {
  events: PropTypes.object.isRequired,
  eventsPath: PropTypes.string,
};

EventCard.defaultProps = {
  eventsPath: "/events",
};

export default EventCard;
